import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { getGalleryImages, uploadGalleryImage, deleteGalleryImage } from '../../api/gallery';
import { Card } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import './ManageGallery.css';

const ManageGallery = () => {
  const { user } = useContext(AuthContext);
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [caption, setCaption] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    try {
      setLoading(true);
      const data = await getGalleryImages();
      setGallery(data);
    } catch (error) {
      showMessage('error', 'Failed to load gallery images');
      console.error('Error fetching gallery:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        showMessage('error', 'Please select an image file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        showMessage('error', 'Image size must be less than 5MB');
        return;
      }
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      showMessage('error', 'Please select an image');
      return;
    }

    try {
      setUploading(true);
      await uploadGalleryImage(selectedFile, caption, user.uid);
      showMessage('success', 'Image uploaded successfully');
      
      // Reset form
      setSelectedFile(null);
      setCaption('');
      setPreviewUrl('');
      document.getElementById('file-input').value = '';
      
      // Refresh gallery
      await fetchGallery();
    } catch (error) {
      showMessage('error', 'Failed to upload image');
      console.error('Error uploading image:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (imageId, storagePath) => {
    try {
      await deleteGalleryImage(imageId, storagePath);
      showMessage('success', 'Image deleted successfully');
      setDeleteConfirm(null);
      await fetchGallery();
    } catch (error) {
      showMessage('error', 'Failed to delete image');
      console.error('Error deleting image:', error);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const cancelUpload = () => {
    setSelectedFile(null);
    setCaption('');
    setPreviewUrl('');
    document.getElementById('file-input').value = '';
  };

  return (
    <div className="manage-gallery">
      <div className="page-header">
        <h1>Manage Gallery</h1>
        <p>Upload and manage photo gallery images</p>
      </div>

      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Upload Section */}
      <Card className="upload-card">
        <h2>Upload New Image</h2>
        <form onSubmit={handleUpload} className="upload-form">
          <div className="file-input-wrapper">
            <input
              id="file-input"
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              disabled={uploading}
            />
            <label htmlFor="file-input" className="file-input-label">
              {selectedFile ? selectedFile.name : 'Choose an image...'}
            </label>
          </div>

          {previewUrl && (
            <div className="image-preview">
              <img src={previewUrl} alt="Preview" />
            </div>
          )}

          <Input
            label="Caption (Optional)"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="Enter image caption"
            disabled={uploading}
          />

          <div className="form-actions">
            <Button
              type="submit"
              variant="primary"
              disabled={!selectedFile || uploading}
            >
              {uploading ? 'Uploading...' : 'Upload Image'}
            </Button>
            {selectedFile && !uploading && (
              <Button
                type="button"
                variant="outline"
                onClick={cancelUpload}
              >
                Cancel
              </Button>
            )}
          </div>
        </form>
      </Card>

      {/* Gallery Grid */}
      <div className="gallery-section">
        <h2>Current Gallery ({gallery.length} images)</h2>
        {loading ? (
          <div className="loading">Loading gallery...</div>
        ) : gallery.length > 0 ? (
          <div className="admin-gallery-grid">
            {gallery.map((image) => (
              <Card key={image.id} className="gallery-card">
                <div className="gallery-image">
                  <img src={image.imageUrl} alt={image.caption || 'Gallery image'} />
                </div>
                <div className="gallery-info">
                  {image.caption && (
                    <p className="gallery-caption-text">{image.caption}</p>
                  )}
                  <p className="gallery-date">
                    {image.createdAt && new Date(image.createdAt.toDate ? image.createdAt.toDate() : image.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <div className="gallery-actions">
                  {deleteConfirm === image.id ? (
                    <div className="delete-confirm">
                      <p>Are you sure?</p>
                      <div className="delete-confirm-actions">
                        <Button
                          variant="danger"
                          size="small"
                          onClick={() => handleDelete(image.id, image.storagePath)}
                        >
                          Yes, Delete
                        </Button>
                        <Button
                          variant="outline"
                          size="small"
                          onClick={() => setDeleteConfirm(null)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button
                      variant="danger"
                      size="small"
                      onClick={() => setDeleteConfirm(image.id)}
                    >
                      Delete
                    </Button>
                  )}
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="empty-state">
            <p>No images in gallery yet. Upload your first image above!</p>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ManageGallery;
